import { alpha, Box, FormControl, MenuItem, Select, SxProps, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { useStyles } from './styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setAutoPlay, setSimulatorOrderDataFunction, setSimulatorTimestamp } from '../../reducer/simulator.reducer'
import { selectTradingCalendarDataByExchange } from '../../selectors/chart'
import { TradingSession } from '../../proto/generated/trading-calendar'
import { getTradingCalenderData } from '../../services/apis/supercharts'
import { selectSimulatorDate } from '../../selectors/simulator'
import { isFirstDateGreatedThanSecond, isSameDate } from '../../util/dateTime'
import commonActions from '../../actions/common.actions'
import { useCombineBasketAnalyzeAndLegOrders } from '../../hooks/useCombineBasketAnalyzeAndLegOrders'
import { createStringDateFromStamp } from '../../util/tradePage'
import { ExtraSmallButton } from '../../components/custom'
import CalendarIcon from '../../assets/icons/CalendarIcon'
import ThinArrowLeftIcon from '../../assets/icons/ThinArrowLeftIcon'
import ThinArrowRightIcon from '../../assets/icons/ThinArrowRightIcon'
import { SIMULATOR_MIN_DATE_NSE, SIMULATOR_MIN_DATE_BSE } from '../../assets/constants'
import { ActionType, useUndoRedoContext } from '../../components/UndoRedoFunctonalities/UndoRedo.context'
import PlayIcon from '../../assets/icons/PlayOutlineIcon'
import ForwardIcon from '../../assets/icons/ForwardIcon'
import PlaySpeedMenu from './PlaySpeedMenu'
import { Exchange } from '../../proto/generated/common'
import { selectPrimarySecurity } from '../../selectors/home'
import { isAuthenticated } from '../../util/authentication'
import PauseIcon from '../../assets/icons/PauseIcon'
import { OrdersDataType } from '../../reducer/orders.reducer'
import { getSecurityKey } from '../../util/security'
import { selectStrikeToOptionsMap, selectUnderlyingOptionsTradeData } from '../../selectors/options'
import { findSimulatorSquareOffOrder } from '../../util/options'
import { OptionType } from '../../proto/generated/security'
import { getCurrentOrderLtpPriceFromData } from '../../util/tradepanel'
import { calculateSimulatorOrdersLiveData } from '../../reducer/options.reducer'
import { v4 as uuid } from 'uuid'
import { optionsTradesDataType, optionTradeDataType } from '../typings/options.typings'

const timeButtonStyles: SxProps = {
    color: 'inherit',
    padding: 0,
    minWidth: '35px',
    backgroundColor: (theme: any) => theme.palette.background.default
}

const timeShiftOptions = [
    {
        label: '1m',
        value: 1
    },
    {
        label: '5m',
        value: 5
    },
    {
        label: '15m',
        value: 15
    },
    {
        label: '30m',
        value: 30
    },
    {
        label: '1h',
        value: 60
    },
    {
        label: '1D',
        value: 'day'
    }
]

const TimeControls = () => {
    const { classes } = useStyles()
    const { primary, underlying } = useSelector(selectPrimarySecurity)
    const dispatch = useDispatch()
    const { push } = useUndoRedoContext()
    const calendarEvents = useSelector(selectTradingCalendarDataByExchange(primary?.exchange ?? Exchange.NSE))
    const date = useSelector(selectSimulatorDate)
    const prevSelectedDate = useRef(date)
    const { orders, ordersPrices, allOrders } = useCombineBasketAnalyzeAndLegOrders()
    const currentDateStr = dayjs(date).format('YYYYMMDD')
    const [speed, setSpeed] = useState(1)
    const [isPlaying, setIsPlaying] = useState(false)
    const intervalRef = useRef<NodeJS.Timeout | null>(null)
    const autoRunIntervalRef = useRef<NodeJS.Timeout | null>(null)
    const eodRef = useRef<number | null>(null)
    const authenticated = isAuthenticated()
    const [selectedTimeShift, setSelectedTimeShift] = useState(timeShiftOptions[0].value)
    const orderIdOrderRef = useRef<Record<string, OrdersDataType>>({})
    const allOrdersRef = useRef<OrdersDataType[]>([])
    const security = underlying || primary
    const underlyingToken = useMemo(() => security ? getSecurityKey(security.exchange, security.code) : '', [security])
    const optionsTradesData = useSelector(selectUnderlyingOptionsTradeData(underlyingToken))
    const optionsTradesDataRef = useRef<optionsTradesDataType[] | undefined>(optionsTradesData)
    const strikeToOptionMap = useSelector(selectStrikeToOptionsMap(underlyingToken))
    const strikeToOptionMapRef = useRef<Record<string, optionTradeDataType>>(strikeToOptionMap)
    const sqOffByOrderIdToOrderMapRef = useRef<Record<string, OrdersDataType>>({})

    const simulatorMinDate = useMemo(() => {
        if (security?.exchange === Exchange.BSE) {
            return SIMULATOR_MIN_DATE_BSE
        }
        return SIMULATOR_MIN_DATE_NSE
    }, [security?.exchange])

    useEffect(()=>{
        optionsTradesDataRef.current = optionsTradesData
    }, [optionsTradesData])

    useEffect(()=>{
        strikeToOptionMapRef.current = strikeToOptionMap
    }, [strikeToOptionMap])

    useEffect(() => {
        const map: Record<string, OrdersDataType> = {}
        orders.forEach(order => {
            if (order.sqOffByOrderId) {
                const sqOrder = findSimulatorSquareOffOrder(order, orders, date)
                if (sqOrder) {
                    map[order.sqOffByOrderId] = sqOrder
                }
            }
        })
        sqOffByOrderIdToOrderMapRef.current = map
    }, [orders, date])

    useEffect(() => {
        const map: Record<string, OrdersDataType> = {}
        allOrders.forEach(order => {
            map[order.uuid] = order
        })
        orderIdOrderRef.current = map
        allOrdersRef.current = allOrders.filter(o => o.traded)
    }, [allOrders])

    useEffect(() => {
        dispatch(setAutoPlay(isPlaying))
    }, [dispatch, isPlaying])

    useEffect(() => {
        prevSelectedDate.current = date
    }, [date])

    const requiredOrders = useMemo(() => {
        const obj = orders.filter((o) => (o.timeStamp ?? 0) <= date && o.traded && !o.sqOffByOrderId && !o.sqOffWithOrderId).sort((a, b) => (a.timeStamp ?? 0) - (b.timeStamp ?? 0))
        return obj
    }, [date, orders])

    const range = (start: number, end: number) => {
        const values: number[] = []
        for (let i = start; i <= end; i++) {
            values.push(i)
        }
        return values
    }

    useEffect(() => {
        const timeoutId = setTimeout(async () => {
            if ((calendarEvents)?.tradingSessions?.length === 0 && authenticated) {
                getTradingCalenderData({ dispatch })
            }
        }, 2000)

        return () => {
            clearTimeout(timeoutId)
        }
    }, [calendarEvents, authenticated])

    const handleExpiredContracts = useCallback((endOfSessionTimeStamp: number) => {
        let newOrders = [...orders]
        let newOrdersPrices = [...ordersPrices]
        newOrders.forEach(order => {
            const sqOrder = sqOffByOrderIdToOrderMapRef.current[order.sqOffByOrderId ?? '']
            if (sqOrder) return
            if (isSameDate(order.expiry, endOfSessionTimeStamp)) {
                const orderIndex = newOrders.findIndex(o => o.uuid === order.uuid)
                const strikeData = strikeToOptionMapRef.current[`${order.expiry}-${order.security?.strikePrice}`]
                const security = order.security
                if (strikeData && security) {
                    const optionData = security.optionType === OptionType.CE ? strikeData.callOption : strikeData.putOption
                    if (optionData) {
                        const type = order.type === 'buy' ? 'sell' : 'buy'
                        newOrders = [...newOrders, {
                            type,
                            security: security,
                            data: strikeData,
                            callput: security.optionType === OptionType.CE ? 'call' : 'put',
                            quantity: newOrders[orderIndex].quantity,
                            expiry: security.expiry?.expiry ?? 0,
                            strike: strikeData.strikeCol.strike,
                            orderDetails: undefined,
                            uuid: uuid(),
                            contractType: 'OPTION',
                            visibility: true,
                            timeStamp: endOfSessionTimeStamp,
                            traded: true,
                            sqOffWithOrderId: newOrders[orderIndex].uuid
                        }]
    
                        const newPrice = getCurrentOrderLtpPriceFromData(newOrders[newOrders.length - 1], optionsTradesDataRef.current ?? [], [])
                        newOrdersPrices = [...newOrdersPrices, {
                            ...newPrice,
                            traded: true
                        }]
    
                        newOrders[orderIndex] = {
                            ...newOrders[orderIndex],
                            sqOffByOrderId: newOrders[newOrders.length - 1].uuid
                        }
                    }
                }
            }
        })
        push({ prev: { orders: orders, ordersPrices: ordersPrices }, new: { orders: newOrders, ordersPrices: newOrdersPrices } }, 'simulator orders changed', ActionType.SIMULATOR_ORDERS_CHANGED)
        setSimulatorOrderDataFunction({ orders: newOrders, orderPrices: newOrdersPrices })
        calculateSimulatorOrdersLiveData(underlyingToken)
    }, [orders, ordersPrices, underlyingToken])

    const changeSimulatorTimestamp = useCallback(
        (timeStamp: number) => {
            if (dayjs(timeStamp).isAfter(dayjs(simulatorMinDate))) {
                if (!isSameDate(prevSelectedDate.current, timeStamp)) {
                    const prevStamp = prevSelectedDate.current
                    const minExpiry = Math.min(...requiredOrders.map((o) => o.expiry))
                    if (isFinite(minExpiry) && isFirstDateGreatedThanSecond(timeStamp, minExpiry)) {
                        commonActions.setApiError(dispatch, {
                            error: true,
                            message: `We have squared off the expired contracts - ${createStringDateFromStamp(minExpiry)}`,
                            errorType: 'info'
                        })
                        dispatch(setSimulatorTimestamp(0))
                        setTimeout(() => {
                            const endOfDayTime = setEndOfDay(true)
                            dispatch(setSimulatorTimestamp(endOfDayTime ?? prevStamp))
                            setTimeout(()=>{
                                handleExpiredContracts(endOfDayTime??0)
                            }, 300)
                        }, 10)
                        return
                    }
                }
                if (!isPlaying) {
                    push({ previousTimeStamp: prevSelectedDate.current, timeStamp: timeStamp }, 'simulator time change', ActionType.SIMULATOR_TIME_CHANGE)
                } else {
                    const normalOrders = allOrdersRef.current.filter(o => !o.sqOffWithOrderId)
                    const sqOrders = allOrdersRef.current.filter(o => o.sqOffWithOrderId)
                    if (normalOrders.length === sqOrders.length) {
                        const maxsqofftimestamp = Math.max(...allOrdersRef.current.map(o => (o.timeStamp ?? 0)))
                        if (maxsqofftimestamp <= timeStamp) {
                            setIsPlaying(false)
                        }
                    }
                }
                dispatch(setSimulatorTimestamp(timeStamp))
            }
        },
        [dispatch, handleExpiredContracts, isPlaying, push, requiredOrders, simulatorMinDate]
    )

    const holidays = useMemo(() => {
        return new Set(calendarEvents?.holidays?.map((e: any) => e.date))
    }, [calendarEvents])

    const getSessionStartEnd = (session: TradingSession, baseDate: dayjs.Dayjs) => {
        const start = baseDate.startOf('day').add(session?.startTime, 'second')
        const end = baseDate.startOf('day').add(session?.endTime, 'second')
        return { start, end }
    }

    const allSessions = calendarEvents?.tradingSessions ?? []
    const specialSession = (calendarEvents?.specialTradingDays ?? []).find((d: any) => d.date === currentDateStr)

    const getSessionHoursForDate = (date: dayjs.Dayjs) => {
        const currentDateStr = date.format('YYYYMMDD')
        const specialSession = calendarEvents?.specialTradingDays?.find((d: any) => d.date === currentDateStr)
        const sessionsForToday = specialSession
            ? specialSession.sessionTypes.map((type: string) => allSessions.find((s: any) => s.type === type)).filter(Boolean)
            : [allSessions.find((s: any) => s.type === 'MORNING_SESSION')].filter(Boolean)
        const allSessionHours: number[] = []
        sessionsForToday.forEach((session: any) => {
            const { start, end } = getSessionStartEnd(session, date)
            for (let hour = start.hour(); hour <= end.hour(); hour++) {
                allSessionHours.push(hour)
            }
        })
        return Array.from(new Set(allSessionHours)).sort((a, b) => a - b)
    }

    const hourOptions = useMemo(() => {
        return getSessionHoursForDate(dayjs(date))
    }, [date, calendarEvents])

    const sessionsForToday = specialSession
        ? specialSession.sessionTypes.map((type: string) => allSessions.find((s: any) => s.type === type)).filter(Boolean)
        : [allSessions.find((s: any) => s.type === 'MORNING_SESSION')].filter(Boolean)

    const session = sessionsForToday[0]

    const { start, end } = getSessionStartEnd(session!, dayjs(date))

    const getValidMinutes = (hour: number) => {
        const toFormatted = (arr: number[]) => arr.map((min) => String(min).padStart(2, '0'))

        if (hour === start.hour() && hour === end.hour()) {
            return toFormatted(range(start.minute(), end.subtract(1, 'minute').minute()))
        } else if (hour === start.hour()) {
            return toFormatted(range(start.minute(), 59))
        } else if (hour === end.hour()) {
            return toFormatted(range(0, end.subtract(1, 'minute').minute()))
        } else {
            return toFormatted(range(0, 59))
        }
    }

    const shouldDisableDate = useCallback(
        (day: dayjs.Dayjs): boolean => {
            const dayStr = day.format('YYYYMMDD')
            const isWeekend = day.day() === 0 || day.day() === 6
            const isHoliday = holidays.has(dayStr)
            const currentMonth = dayjs().startOf('month')
            const futureMonth = day.isAfter(currentMonth, 'month')
            const specialDay = calendarEvents?.specialTradingDays?.find((e: any) => e.date === dayStr)
            const hasSpecialSession = specialDay && Array.isArray(specialDay.sessionTypes) && specialDay.sessionTypes.length > 0
            if (hasSpecialSession) {
                return false
            }
            return (isWeekend && !hasSpecialSession) || isHoliday || futureMonth
        },
        [calendarEvents?.specialTradingDays, holidays]
    )

    const getDatesFromCurrentToEnd = (startDate: dayjs.Dayjs) => {
        const sessionEndDate = dayjs().subtract(1, 'day').startOf('day')
        const dates: string[] = []
        let tempDate = startDate.startOf('day')

        while (tempDate.isSame(sessionEndDate) || tempDate.isBefore(sessionEndDate)) {
            if (!shouldDisableDate(tempDate)) {
                dates.push(tempDate.format('MMM DD, YYYY'))
            }
            tempDate = tempDate.add(1, 'day')
        }

        return dates
    }
    useEffect(() => {
        const currentDate = dayjs(finalValidDate)
        changeSimulatorTimestamp(currentDate.valueOf())
    }, [])

    const handleDateChange = (date: dayjs.Dayjs | null) => {
        if (date) {
            changeSimulatorTimestamp(date.valueOf())
        }
    }
    const updateTime = (offsetInMinutes: number) => {
        if (!date) return
        const baseDate = dayjs(date)
        const session = sessionsForToday[0]
        if (!session) return
        const { start, end } = getSessionStartEnd(session, baseDate)
        const currentTime = baseDate
        const tradingMinutes = end.diff(start, 'minute')
        const currentOffset = currentTime.diff(start, 'minute')
        let newOffset = currentOffset + offsetInMinutes

        const skipDisabledDates = (date: dayjs.Dayjs, direction: 'forward' | 'backward', previousSelectedDate: dayjs.Dayjs) => {
            let newDate = date
            while (shouldDisableDate(newDate)) {
                newDate = direction === 'forward' ? newDate.add(1, 'day') : newDate.subtract(1, 'day')
            }
            if (newDate.isSame(dayjs(), 'day') || newDate.isAfter(dayjs(), 'day')) return previousSelectedDate
            return newDate
        }

        if (newOffset < 0) {
            const newDate = baseDate.subtract(1, 'day')
            const validDate = skipDisabledDates(newDate, 'backward', baseDate)
            const prevDateStr = validDate.format('YYYYMMDD')
            let prevSession: any
            const specialPrev = calendarEvents?.specialTradingDays.find((day: any) => day.date === prevDateStr)
            if (specialPrev?.sessionTypes?.length) {
                const sessionType = specialPrev.sessionTypes[0]
                prevSession = allSessions.find((s: any) => s.type === sessionType)
            } else {
                prevSession = allSessions.find((s: any) => s.type === 'MORNING_SESSION')
            }
            if (!prevSession) return
            const { end: prevEnd } = getSessionStartEnd(prevSession, validDate)
            // const prevTradingMinutes = prevEnd.subtract(1, 'minute').diff(prevStart, 'minute') + 1
            // const adjustedOffset = prevTradingMinutes + newOffset
            // const newTimestamp = prevStart.add(adjustedOffset, 'minute').valueOf()
            const newTimestamp = prevEnd.subtract(1, 'minute').valueOf()
            changeSimulatorTimestamp(newTimestamp)
        } else if (newOffset >= tradingMinutes) {
            const newDate = baseDate.add(1, 'day')
            const validDate = skipDisabledDates(newDate, 'forward', baseDate)
            const nextDateStr = validDate.format('YYYYMMDD')
            const nextSessionType = calendarEvents?.specialTradingDays.find((d: any) => d.date === nextDateStr)?.sessionTypes?.[0]

            const nextSession = nextSessionType ? allSessions.find((s: any) => s.type === nextSessionType) : allSessions.find((s: any) => s.type === 'MORNING_SESSION')
            if (!nextSession) return

            const { start: nextStart } = getSessionStartEnd(nextSession, validDate)
            // const adjustedOffset = newOffset - tradingMinutes
            // const newTimestamp = nextStart.add(adjustedOffset, 'minute').valueOf()
            const newTimestamp = nextStart.valueOf()
            changeSimulatorTimestamp(newTimestamp)
        } else {
            const newTimestamp = start.add(newOffset, 'minute').valueOf()
            changeSimulatorTimestamp(newTimestamp)
        }
    }

    const setStartOfDay = useCallback(() => {
        const baseDate = dayjs(date)
        const session = sessionsForToday[0]
        if (!session) return
        const { start } = getSessionStartEnd(session, baseDate)
        const startMarketTime = start.add(1, 'minute')
        const startOfDayTimestamp = startMarketTime.second(0).millisecond(0).valueOf()
        changeSimulatorTimestamp(startOfDayTimestamp)
    }, [date, sessionsForToday, changeSimulatorTimestamp])

    const setEndOfDay = useCallback((returnTime?: Boolean) => {
        const baseDate = dayjs(date)
        if (!sessionsForToday.length) return
        const lastSession = sessionsForToday[sessionsForToday.length - 1]
        const { end } = getSessionStartEnd(lastSession, baseDate)
        const correctedEnd = end.subtract(1, 'minute')
        const endOfDayTimestamp = correctedEnd.second(0).millisecond(0).valueOf()
        eodRef.current = correctedEnd.valueOf()
        if (returnTime) {
            return endOfDayTimestamp
        }
        changeSimulatorTimestamp(endOfDayTimestamp)
    }, [date, sessionsForToday, changeSimulatorTimestamp])

    // const getSessionEndTime = useCallback((date: any) => {
    //     const today = dayjs(date).format('YYYYMMDD');
    //     const specialSession = calendarEvents?.specialTradingDays.find((event: any) => event.date === today);
    //     if (specialSession) {
    //         const specialSessionType = specialSession.sessionTypes[0];
    //         const sessionData = calendarEvents?.tradingSessions.find((session: any) => session.type === specialSessionType);
    //         if (sessionData) {
    //             return dayjs().startOf('day').add(sessionData.endTime, 'seconds');
    //         }
    //     }
    // }, [calendarEvents?.specialTradingDays, calendarEvents?.tradingSessions])

    const isButtonDisabled = (minutes: number) => {
        const currentDate = dayjs(date)
        const addedTime = currentDate.add(minutes, 'minute')
        const sessionEndDate = dayjs().subtract(1, 'day')
        const sessionEndTime = sessionEndDate.hour(15).minute(29).second(0).millisecond(0)
        return currentDate.isSame(sessionEndDate, 'day') && addedTime.isAfter(sessionEndTime)
    }

    const findLastValidDate = useCallback(
        (startDate: dayjs.Dayjs): dayjs.Dayjs => {
            const minDate = dayjs(simulatorMinDate).startOf('day')
            let dateToCheck = startDate
            while (shouldDisableDate(dateToCheck)) {
                dateToCheck = dateToCheck.subtract(1, 'day')
                if (dateToCheck.isBefore(minDate)) return minDate
            }
            return dateToCheck
        },
        [shouldDisableDate, simulatorMinDate]
    )
    const selectedDayjs = useMemo(() => (date ? dayjs(date) : null), [date])
    const [finalValidDate, setFinalValidDate] = useState(selectedDayjs ? findLastValidDate(selectedDayjs) : null)
    useEffect(() => {
        setFinalValidDate(selectedDayjs ? findLastValidDate(selectedDayjs) : null)
    }, [findLastValidDate, selectedDayjs])

    const findNextValidDate = (startDate: dayjs.Dayjs): dayjs.Dayjs | null => {
        let dateToCheck = startDate.add(1, 'day')
        while (shouldDisableDate(dateToCheck)) {
            dateToCheck = dateToCheck.add(1, 'day')
            if (!dateToCheck.isBefore(dayjs(), 'day')) return null
        }
        if (!dateToCheck.isBefore(dayjs(), 'day')) {
            return null
        }
        return dateToCheck
    }
    const updateTimeAutoRun = (offsetInMinutes: number) => {
        if (!date) return
        let currentTime = dayjs(date)
        const stopAutoRun = () => {
            if (autoRunIntervalRef.current) {
                clearInterval(autoRunIntervalRef.current)
                autoRunIntervalRef.current = null
            }
            setIsPlaying(false)
        }

        const autoRun = () => {
            const baseDate = currentTime.startOf('day')
            const session = sessionsForToday[0]
            if (!session) {
                const nextValid = findNextValidDate(baseDate)
                if (nextValid) {
                    currentTime = nextValid.startOf('day')
                } else {
                    stopAutoRun()
                }
                return
            }
            const { start, end } = getSessionStartEnd(session, baseDate)
            if (currentTime.isBefore(start)) {
                currentTime = start
            }
            const newTimestamp = currentTime.valueOf()

            // if (till === 'Custom') {
            //     if (!customEndTimeRef.current) {
            //         stopAutoRun();
            //         return;
            //     }
            //     const customEndTime = dayjs(customEndTimeRef.current);
            //     if (currentTime.isAfter(customEndTime)) {
            //         stopAutoRun();
            //         return;
            //     }
            // } else {
            if (eodRef.current && newTimestamp > dayjs(eodRef.current).valueOf()) {
                stopAutoRun()
                return
            }
            // }
            if (currentTime.isBefore(end)) {
                changeSimulatorTimestamp(newTimestamp)
                currentTime = currentTime.add(offsetInMinutes, 'minute')
            } else {
                const nextValid = findNextValidDate(baseDate)
                if (nextValid) {
                    currentTime = nextValid.startOf('day')
                } else {
                    stopAutoRun()
                }
            }
        }

        const startAutoRun = () => {
            if (autoRunIntervalRef.current) return
            autoRunIntervalRef.current = setInterval(autoRun, speed >= 1 ? 1000 : speed === 0.5 ? 2000 : 5000)
        }
        startAutoRun()
    }

    const startPlaying = () => {
        if (autoRunIntervalRef.current) return
        const offsetInMinutes = selectedTimeShift === 'day' ? 24 * 60 : Number(selectedTimeShift)
        updateTimeAutoRun(speed <= 1 ? offsetInMinutes : offsetInMinutes * speed)
        setIsPlaying(true)
    }

    const stopPlaying = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
        }
        if (autoRunIntervalRef.current) {
            clearInterval(autoRunIntervalRef.current)
            autoRunIntervalRef.current = null
        }
    }

    useEffect(() => {
        if (isPlaying) {
            startPlaying()
        } else {
            stopPlaying()
        }
        return () => stopPlaying()
    }, [isPlaying])

    // useEffect(() => {
    //     if (till === 'Custom' && formattedSelectedDate && customHour != null && customMinute != null) {
    //         const combined = dayjs(formattedSelectedDate, 'MMM DD, YYYY')
    //             .hour(customHour)
    //             .minute(customMinute as any)
    //             .second(0)
    //             .millisecond(0);

    //         customEndTimeRef.current = combined.valueOf();
    //     }
    // }, [formattedSelectedDate, customHour, customMinute, till]);

    // useEffect(() => {
    //     if (till === 'Till EOD') {
    //         const baseDate = dayjs(finalValidDate);
    //         const lastSession = sessionsForToday[sessionsForToday.length - 1];
    //         const { end } = getSessionStartEnd(lastSession, baseDate);
    //         const correctedEnd = end.subtract(1, 'minute').second(0).millisecond(0);
    //         setCustomHour(correctedEnd.hour());
    //         setCustomMinute(correctedEnd.minute().toString().padStart(2, '0'));
    //     }
    // }, [till, date, specialSession, allSessions]);

    const goToPreviousDay = useCallback(() => {
        const prevDate = findLastValidDate(dayjs(date).subtract(1, 'day'))
        changeSimulatorTimestamp(prevDate.valueOf())
    }, [changeSimulatorTimestamp, date, findLastValidDate])

    const nextDayStart = findNextValidDate(dayjs(date))

    const goToNextDay = useCallback(() => {
        if (!nextDayStart) return
        const newDate = nextDayStart
        changeSimulatorTimestamp(newDate.valueOf())
    }, [changeSimulatorTimestamp, nextDayStart])

    return (
        <Box className={classes.simulatorTimeControls}>
            <Box
                sx={{
                    width: '100%',
                    mx: 'auto'
                }}
            >
                <Box
                    mt={1}
                    mb={2}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={1}
                    sx={{
                        width: '100%'
                    }}
                >
                    {/* <Box sx={{ flexGrow: 1 }}> */}
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            // sx={{ flexGrow: 1 }}
                            value={finalValidDate}
                            onChange={handleDateChange}
                            shouldDisableDate={shouldDisableDate}
                            maxDate={dayjs().subtract(1, 'day')}
                            minDate={dayjs(simulatorMinDate)}
                            format="MMM D, YY"
                            slots={{
                                openPickerIcon: CalendarIcon
                            }}
                            slotProps={{
                                day: () => ({
                                    sx: {
                                        '&.MuiPickersDay-today:not(.Mui-selected)': {
                                            border: 'none'
                                        }
                                    }
                                }),
                                textField: {
                                    fullWidth: true,
                                    variant: 'outlined',
                                    placeholder: 'Select date',
                                    InputProps: {
                                        sx: {
                                            fontSize: 14,
                                            cursor: 'pointer',
                                            height: '32px'
                                        }
                                    },
                                    InputLabelProps: {
                                        shrink: true
                                    },
                                    sx: {
                                        width: '130px',
                                        minWidth: '120px',
                                        height: '32px',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            border: 'none'
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            border: 'none'
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            border: 'none'
                                        },
                                        '&:hover': {
                                            backgroundColor: (theme) => alpha(theme.palette.text.primary, theme.palette.action.hoverOpacity)
                                        },
                                        '.MuiInputBase-input': {
                                            minWidth: '75px',
                                            paddingLeft: '10px'
                                        },
                                        borderRadius: '4px',
                                        backgroundColor: (theme) => theme.palette.background.default
                                    }
                                },
                                openPickerButton: {
                                    sx: { display: 'none' }
                                }
                            }}
                        />
                    </LocalizationProvider>
                    {/* </Box> */}

                    <FormControl size="small">
                        <Select
                            value={dayjs(date).hour()}
                            onChange={(e) => {
                                const newHour = e.target.value
                                const newTimestamp = dayjs(date)
                                    .hour(newHour as any)
                                    .minute(dayjs(date).minute())
                                    .valueOf()
                                changeSimulatorTimestamp(newTimestamp)
                            }}
                            IconComponent={() => <></>}
                            sx={{
                                height: '32px',
                                minWidth: 35,
                                '& .MuiSelect-outlined': { px: '10px !important', textAlign: 'center' },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    border: 'none'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    border: 'none'
                                },
                                '&:hover': {
                                    backgroundColor: (theme) => alpha(theme.palette.text.primary, theme.palette.action.hoverOpacity)
                                },
                                backgroundColor: (theme) => theme.palette.background.default
                            }}
                        >
                            {hourOptions.map((h) => (
                                <MenuItem key={h} value={h}>
                                    {h}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small">
                        <Select
                            value={dayjs(date).minute().toString().padStart(2, '0')}
                            onChange={(e) => {
                                const newMinute = e.target.value
                                const newTimestamp = dayjs(date)
                                    .hour(dayjs(date).hour())
                                    .minute(newMinute as any)
                                    .valueOf()
                                changeSimulatorTimestamp(newTimestamp)
                            }}
                            IconComponent={() => <></>}
                            sx={{
                                height: '32px',
                                minWidth: 35,
                                '& .MuiSelect-outlined': { px: '10px !important', textAlign: 'center' },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    border: 'none'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    border: 'none'
                                },
                                '&:hover': {
                                    backgroundColor: (theme) => alpha(theme.palette.text.primary, theme.palette.action.hoverOpacity)
                                },
                                backgroundColor: (theme) => theme.palette.background.default
                            }}
                        >
                            {getValidMinutes(dayjs(date).hour()).map((m) => (
                                <MenuItem key={m} value={m}>
                                    {m}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <PlaySpeedMenu speed={speed} setSpeed={setSpeed} />

                    <FormControl size="small">
                        <ExtraSmallButton
                            onClick={(e) => {
                                e.stopPropagation()
                                setIsPlaying((prev) => !prev)
                            }}
                            sx={{ color: 'inherit', padding: 0, minWidth: '32px', backgroundColor: (theme) => theme.palette.background.default }}
                        >
                            {isPlaying ? <PauseIcon /> : <PlayIcon />}
                        </ExtraSmallButton>
                    </FormControl>
                </Box>
                <Box display="flex" justifyContent="center" gap={1} alignItems="center" flexWrap="wrap">
                    <ToggleButtonGroup
                        size="small"
                        value={selectedTimeShift}
                        aria-label="text alignment"
                        sx={{
                            height: '32px',
                            '& .Mui-selected': {
                                backgroundColor: (theme) => `${theme.palette.background.default} !important`,
                                '&:hover': {
                                    backgroundColor: (theme) => `${theme.palette.background.default} !important`
                                }
                            },
                            '& .MuiButtonBase-root': {
                                maxWidth: '40px'
                            }
                        }}
                    >
                        {timeShiftOptions.map((m) => (
                            <ToggleButton key={m.value} aria-label="left aligned" selected={m.value === selectedTimeShift} value={m.value} onClick={() => setSelectedTimeShift(m.value)}>
                                {m.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                    <ExtraSmallButton onClick={setStartOfDay} sx={timeButtonStyles}>
                        <Typography fontSize="12px" sx={{ WebkitTransform: 'scaleX(-1)', transform: 'scaleX(-1)' }}>
                            {/* SOD */}
                            <ForwardIcon />
                        </Typography>
                    </ExtraSmallButton>
                    <ExtraSmallButton
                        onClick={() => {
                            if (typeof selectedTimeShift === 'number') {
                                updateTime(-1 * selectedTimeShift)
                            } else {
                                goToPreviousDay()
                            }
                        }}
                        sx={timeButtonStyles}
                    >
                        <Typography fontSize="12px">
                            <ThinArrowLeftIcon />
                        </Typography>
                    </ExtraSmallButton>
                    <ExtraSmallButton
                        onClick={() => {
                            if (typeof selectedTimeShift === 'number') {
                                updateTime(selectedTimeShift)
                            } else {
                                goToNextDay()
                            }
                        }}
                        sx={timeButtonStyles}
                        disabled={typeof selectedTimeShift === 'number' ? isButtonDisabled(selectedTimeShift) : !nextDayStart}
                    >
                        <Typography fontSize="12px">
                            <ThinArrowRightIcon />
                        </Typography>
                    </ExtraSmallButton>
                    <ExtraSmallButton onClick={() => setEndOfDay()} sx={timeButtonStyles}>
                        <Typography fontSize="12px">
                            {/* EOD */}
                            <ForwardIcon />
                        </Typography>
                    </ExtraSmallButton>
                </Box>
            </Box>
        </Box>
    )
}

export default TimeControls
